import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { X, Clock, AlertCircle, AlertTriangle, CheckCircle } from 'lucide-react';

export default function RetroactiveModal({ isOpen, onClose, employeId, employeNom, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [typesConge, setTypesConge] = useState([]);
  const [nombreJours, setNombreJours] = useState(0);
  const [ok, setOk] = useState(false);

  const [formData, setFormData] = useState({
    ID_TypeConge: '', DateDebut: '', DateFin: '',
    EstDemiJourneeDebut: false, EstDemiJourneeFin: false, Commentaire: 'Saisie rétroactive'
  });

  useEffect(() => {
    if (isOpen) {
      fetchTypesConge();
      setFormData({ ID_TypeConge: '', DateDebut: '', DateFin: '', EstDemiJourneeDebut: false, EstDemiJourneeFin: false, Commentaire: 'Saisie rétroactive' });
      setNombreJours(0); setError(''); setOk(false);
    }
  }, [isOpen, employeId]);

  const fetchTypesConge = async () => {
    try { const res = await api.get('/typesconge'); setTypesConge(res.data.typesconge || []); } catch (err) {}
  };

  useEffect(() => {
    if (formData.DateDebut && formData.DateFin && new Date(formData.DateFin) >= new Date(formData.DateDebut)) {
      api.post('/demandes/calculer-jours', { DateDebut: formData.DateDebut, DateFin: formData.DateFin, ID_Pays: 1 })
        .then(res => {
          if (res.data.success) {
            let j = res.data.joursOuvrables;
            if (formData.EstDemiJourneeDebut) j -= 0.5;
            if (formData.EstDemiJourneeFin) j -= 0.5;
            setNombreJours(j > 0 ? j : 0);
          }
        }).catch(() => {});
    } else { setNombreJours(0); }
  }, [formData.DateDebut, formData.DateFin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading || ok) return;
    if (!formData.ID_TypeConge || !formData.DateDebut || !formData.DateFin) { setError('Champs obligatoires.'); return; }
    if (formData.DateFin < formData.DateDebut) { setError('Date fin < début.'); return; }

    setLoading(true); setError('');
    try {
      const res = await api.post('/demandes/retroactive', { ...formData, ID_Employe: employeId });
      if (res.data.success) {
        setOk(true);
        onSuccess?.();
      } else {
        setError(res.data.message || 'Erreur serveur');
      }
    } catch (err) {
      setError('Erreur réseau ou serveur');
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  // ✅ ÉCRAN DE SUCCÈS
  if (ok) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
        <div className="bg-white rounded-2xl p-8 max-w-sm w-full text-center shadow-2xl mx-4" onClick={e => e.stopPropagation()}>
          <CheckCircle size={56} className="text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Enregistré !</h2>
          <p className="text-gray-500 mb-1">{nombreJours} jour(s) pour {employeNom}</p>
          <p className="text-xs text-gray-400 mb-6">Le solde a été déduit automatiquement.</p>
          <button onClick={onClose} className="w-full px-4 py-3 bg-amber-500 text-white rounded-xl font-medium hover:bg-amber-600">Fermer</button>
        </div>
      </div>
    );
  }

  // ✅ FORMULAIRE NORMAL
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-t-2xl sm:rounded-xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white rounded-t-2xl border-b px-5 py-4 flex justify-between items-center z-10">
          <h3 className="text-lg font-semibold text-amber-600">📅 Saisie rétroactive</h3>
          <button onClick={onClose}><X size={22} className="text-gray-400"/></button>
        </div>

        <div className="px-5 py-3 bg-amber-50 border-b border-amber-200 flex items-center gap-2">
          <AlertTriangle size={16} className="text-amber-600" />
          <span className="text-sm text-amber-700">Saisie pour <strong>{employeNom}</strong> • Approuvé auto</span>
        </div>

        {error && (
          <div className="px-5 py-3 bg-red-50 border-b border-red-200 flex items-center gap-2">
            <AlertCircle size={16} className="text-red-500" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type de congé *</label>
            <select className="w-full p-3 border rounded-xl text-sm" value={formData.ID_TypeConge} onChange={e => setFormData({...formData, ID_TypeConge: e.target.value})} required>
              <option value="">Choisir...</option>
              {typesConge.map(t => <option key={t.ID_TypeConge} value={t.ID_TypeConge}>{t.Code} - {t.Libelle}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Date début *</label><input type="date" className="w-full p-3 border rounded-xl text-sm" value={formData.DateDebut} onChange={e => setFormData({...formData, DateDebut: e.target.value})} min="2026-01-01" required /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Date fin *</label><input type="date" className="w-full p-3 border rounded-xl text-sm" value={formData.DateFin} onChange={e => setFormData({...formData, DateFin: e.target.value})} min={formData.DateDebut || '2026-01-01'} required /></div>
          </div>

          <div className="flex gap-4">
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.EstDemiJourneeDebut} onChange={e => setFormData({...formData, EstDemiJourneeDebut: e.target.checked})} /><span className="text-xs">½ journée début</span></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.EstDemiJourneeFin} onChange={e => setFormData({...formData, EstDemiJourneeFin: e.target.checked})} /><span className="text-xs">½ journée fin</span></label>
          </div>

          {nombreJours > 0 && (
            <div className="flex items-center gap-2 p-3 rounded-xl bg-gray-50 text-gray-700"><Clock size={16} /><span className="text-sm font-medium">{nombreJours} jour(s) ouvré(s)</span></div>
          )}

          <div><label className="block text-sm font-medium text-gray-700 mb-1">Commentaire</label><textarea className="w-full p-3 border rounded-xl text-sm" rows={2} value={formData.Commentaire} onChange={e => setFormData({...formData, Commentaire: e.target.value})} /></div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium">Annuler</button>
            <button type="submit" disabled={loading} className="flex-1 px-4 py-3 bg-amber-500 text-white rounded-xl text-sm font-medium disabled:opacity-50">{loading ? 'Enregistrement...' : 'Enregistrer'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}